/* ------------------------------------------------------------------
   Red Light Hero – Compare products section
   Renders the comparison grid for sections/rlh-product-compare.liquid.

   The section outputs three JSON blocks the script reads:
     [data-rlh-config]   settings, labels and the comparison rows (blocks)
     [data-rlh-products] live product data from Shopify (price, image, …) and
                         each product's compare.* metafields ("compare")
     [data-rlh-specs]    fallback comparison data (snippets/rlh-compare-data.liquid)

   Each row's value comes from, in order:
     1. the product's compare.<key> metafield (e.g. compare.irradiance),
        edited on the product in Shopify admin, or
     2. the same key in the data snippet, or
     3. a line of the product's spec sheet metafield that starts with the
        row's "match" text (e.g. "Irradiance"), or
     4. a product metafield (rendered HTML), when the row's source is "metafield".

   Multi-line text is shown one line per value. A blank line starts a new
   group, and a group's first line is its label when it ends with ":" or
   is followed by a measurement, e.g.
     Solar meter
     209 mW/cm² @ 7.5cm
     172 mW/cm² @ 15cm
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  // Same caret Dawn draws in its own <select> fields
  var CARET = '<span class="svg-wrapper"><svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/></svg></span>';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function parseJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // HTML (from metafield_tag) -> plain text, one line per block element.
  function htmlToText(html) {
    if (!html) return '';
    var src = String(html).replace(/<\/(p|li|div|h[1-6]|tr|dt|dd)>|<br\s*\/?>/gi, '\n');
    var doc = new DOMParser().parseFromString(src, 'text/html');
    return doc.body.textContent || '';
  }

  function specLines(html) {
    return htmlToText(html).split('\n').map(function (l) { return l.trim(); }).filter(Boolean);
  }

  function tidy(v) {
    return v.replace(/mw\s*\/\s*cm(2|²)/gi, 'mW/cm²');
  }

  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  // compare.* metafield keys and the older data-snippet keys they replace,
  // so rows set up with either name keep working.
  var ALIAS = { best_for: 'best', treatment_area: 'area', best: 'best_for', area: 'treatment_area' };

  function isFigure(line) { return /^[~≈<>≤≥]?\s*\d/.test(line); }

  // Multi-line text -> list of { label, values } groups, one per blank-line
  // separated block. A block's first line is its label when it ends with ":"
  // or is followed by a measurement line. A single line stays plain text.
  function parseLines(text) {
    var blocks = String(text).replace(/\r/g, '').split(/\n\s*\n/).map(function (b) {
      return b.split('\n').map(function (l) { return l.trim(); }).filter(Boolean);
    }).filter(function (b) { return b.length; });
    if (!blocks.length) return '';
    if (blocks.length === 1 && blocks[0].length === 1) return tidy(blocks[0][0]);
    return blocks.map(function (lines) {
      var label = '';
      if (lines.length > 1 && (/:$/.test(lines[0]) || (!isFigure(lines[0]) && isFigure(lines[1])))) {
        label = lines.shift().replace(/:$/, '').trim();
      }
      return { label: label, values: lines.map(tidy) };
    });
  }

  // Rich text metafield (JSON tree) -> plain text, one line per paragraph or list item
  function richToText(node) {
    if (!node) return '';
    if (node.type === 'text') return node.value || '';
    var inner = (node.children || []).map(richToText).join('');
    if (node.type === 'paragraph' || node.type === 'heading') return '\n' + inner + '\n';
    if (node.type === 'list-item') return inner + '\n';
    return inner;
  }

  // A comparison value in any of the shapes a metafield or the data snippet can
  // give: text (possibly multi-line), a list of strings, a list of
  // { label, values } groups, or a rich text metafield.
  function normalise(v) {
    if (v == null || v === '') return '';
    if (typeof v === 'string') return v.indexOf('\n') === -1 ? tidy(v.trim()) : parseLines(v);
    if (Array.isArray(v)) {
      if (!v.length) return '';
      if (typeof v[0] === 'string') return parseLines(v.join('\n'));
      return v.filter(function (g) { return g && g.values && g.values.length; });
    }
    if (typeof v === 'object' && v.type === 'root') return parseLines(richToText(v));
    return tidy(String(v));
  }

  // Comparison text for a row key: the product's compare.* metafield first,
  // then the data snippet, trying the key's older name as well.
  function lookup(p, key) {
    if (!key) return '';
    var alt = ALIAS[key];
    var v = normalise(p.compare[key]);
    if (!v && alt) v = normalise(p.compare[alt]);
    if (!v) v = normalise(p.spec[key]);
    if (!v && alt) v = normalise(p.spec[alt]);
    return v;
  }

  // Pull "Label: value" lines from the spec sheet whose label starts with
  // one of the match prefixes ("Irradiance", "Input power|Power", …).
  // Returns a string, or a list of { label, values } groups when there are
  // several matching lines or the value is split onto several lines.
  function fromSpecSheet(p, match, split) {
    if (!match || !p.specLines.length) return '';
    var prefixes = match.split('|').map(function (m) { return m.trim().toLowerCase(); }).filter(Boolean);
    var groups = [];
    p.specLines.forEach(function (line) {
      var low = line.toLowerCase();
      var hit = prefixes.some(function (m) { return low.indexOf(m) === 0; });
      if (!hit) return;
      var idx = line.indexOf(':');
      if (idx === -1) return;
      var label = line.slice(0, idx).trim();
      var val = tidy(line.slice(idx + 1).trim());
      if (!val) return;
      var sub = (label.match(/\(([^)]+)\)/) || [])[1] || '';
      groups.push({ label: sub ? cap(sub) : '', line: label, values: split ? val.split(/,\s+/) : [val] });
    });
    if (!groups.length) return '';
    if (groups.length > 1) {
      groups.forEach(function (g) { if (!g.label) g.label = g.line; });
    }
    if (groups.length === 1 && !groups[0].label && groups[0].values.length === 1) return groups[0].values[0];
    return groups;
  }

  function resolve(p, row) {
    var v = '';
    if (row.source === 'metafield') {
      v = p.mf && p.mf[row.metafield] ? { html: p.mf[row.metafield] } : '';
      if (v && !htmlToText(v.html).trim()) v = '';
    } else if (row.source === 'specsheet') {
      v = fromSpecSheet(p, row.match, row.split);
    } else {
      v = lookup(p, row.key);
      if (!v && row.match) v = fromSpecSheet(p, row.match, row.split);
    }
    return v || '';
  }

  function renderValue(v) {
    if (!v) return '';
    if (typeof v === 'string') return esc(v);
    if (v.html) return v.html;
    if (Array.isArray(v)) {
      return v.map(function (g) {
        return (g.label ? '<span class="rlh-compare__irr-label">' + esc(g.label) + '</span>' : '') +
          '<ul class="rlh-compare__irr">' + g.values.map(function (x) { return '<li>' + esc(x) + '</li>'; }).join('') + '</ul>';
      }).join('');
    }
    return esc(String(v));
  }

  function plainValue(v) {
    if (!v) return '';
    if (typeof v === 'string') return v.replace(/\s+/g, ' ').trim();
    if (v.html) return htmlToText(v.html).replace(/\s+/g, ' ').trim();
    if (Array.isArray(v)) return v.map(function (g) { return g.label + ':' + g.values.join('|'); }).join(';');
    return String(v);
  }

  function init(root) {
    if (root.dataset.rlhReady) return;
    root.dataset.rlhReady = '1';

    var cfg = parseJSON(root.querySelector('[data-rlh-config]')) || {};
    var products = parseJSON(root.querySelector('[data-rlh-products]')) || [];
    var specs = parseJSON(root.querySelector('[data-rlh-specs]')) || {};
    var labels = cfg.labels || {};
    var rows = (cfg.rows || []).filter(function (r) { return r.group === 'both' || r.group === cfg.type; });

    if (products.length < 2 || !rows.length) { root.hidden = true; return; }

    // Keep the merchant's order (product list order, or the built-in list)
    var order = cfg.order || [];
    products.sort(function (a, b) {
      var ia = order.indexOf(a.handle), ib = order.indexOf(b.handle);
      return (ia === -1 ? 1e9 : ia) - (ib === -1 ? 1e9 : ib);
    });

    var byHandle = {};
    products.forEach(function (p) {
      p.spec = specs[p.handle] || {};
      p.compare = p.compare || {};
      p.name = p.compare.name || p.spec.name || p.title;
      p.tagline = p.compare.tagline || p.spec.tagline || '';
      p.alts = (p.compare.alts && p.compare.alts.length) ? p.compare.alts : (p.spec.alts || []);
      p.specLines = specLines(p.specSheet);
      byHandle[p.handle] = p;
    });

    var current = byHandle[cfg.current] ? cfg.current : null;
    // "table" mode (compare-table section): every product side by side in a
    // scrolling table, columns toggled with chips, nothing pinned.
    var table = cfg.mode === 'table';
    var allHandles = products.map(function (p) { return p.handle; });
    var cols = Math.max(2, Math.min(cfg.columns || 3, products.length));
    var mobileCols = Math.min(2, cols);

    var picks = [];
    if (table) {
      picks = cfg.initial > 0 ? allHandles.slice(0, cfg.initial) : allHandles.slice();
    } else {
      // Starting columns: the product being viewed, its suggested comparisons, then the rest
      if (current) {
        picks.push(current);
        byHandle[current].alts.forEach(function (h) {
          if (byHandle[h] && picks.indexOf(h) === -1) picks.push(h);
        });
      }
      products.forEach(function (p) { if (picks.indexOf(p.handle) === -1) picks.push(p.handle); });
      picks = picks.slice(0, cols);
    }

    var grid = root.querySelector('[data-rlh-grid]');
    grid.style.setProperty('--rlh-cols', table ? Math.max(1, picks.length) : cols);
    grid.style.setProperty('--rlh-cols-m', mobileCols);

    var pickerEl = root.querySelector('[data-rlh-picker]');
    function renderPicker() {
      if (!pickerEl) return;
      pickerEl.innerHTML = '<span class="rlh-compare__picker-label">' + esc(labels.picker) + '</span>' +
        products.map(function (p) {
          var on = picks.indexOf(p.handle) !== -1;
          return '<button type="button" class="rlh-compare__chip" data-chip="' + esc(p.handle) + '" aria-pressed="' + on + '">' + esc(p.name) + '</button>';
        }).join('') +
        '<span class="rlh-compare__picker-actions">' +
        '<button type="button" data-chips="all">' + esc(labels.all) + '</button>' +
        '<button type="button" data-chips="none">' + esc(labels.clear) + '</button></span>';
    }
    if (pickerEl) {
      pickerEl.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-chip]');
        var act = e.target.closest('[data-chips]');
        if (chip) {
          var h = chip.getAttribute('data-chip');
          var at = picks.indexOf(h);
          if (at === -1) picks.push(h); else picks.splice(at, 1);
          picks = allHandles.filter(function (x) { return picks.indexOf(x) !== -1; });
        } else if (act) {
          picks = act.getAttribute('data-chips') === 'all' ? allHandles.slice() : [];
        } else {
          return;
        }
        renderPicker();
        render();
      });
    }

    var toggle = root.querySelector('[data-rlh-diff]');
    if (toggle) {
      toggle.addEventListener('change', function () {
        root.classList.toggle('is-diff-only', toggle.checked);
      });
    }

    function stars(p) {
      if (!cfg.showRating) return '';
      if (!p.rating) return '<div class="rlh-compare__rating"></div>';
      var r = parseFloat(p.rating);
      return '<div class="rlh-compare__rating"><span class="rlh-compare__stars" aria-hidden="true">★★★★★<span style="width:' + (r / 5 * 100) + '%">★★★★★</span></span>' +
        '<span>' + r.toFixed(1) + (p.ratingCount ? ' (' + esc(p.ratingCount) + ')' : '') +
        '<span class="visually-hidden"> out of 5 stars</span></span></div>';
    }

    function card(p, i) {
      var isCurrent = p.handle === current && (table || i === 0);
      var top;
      if (isCurrent) {
        top = '<div class="rlh-compare__top"><span class="rlh-compare__badge">' + esc(labels.badge) + '</span></div>';
      } else if (table) {
        // no dropdowns in a table; keep the cards level when one of them has the badge
        top = current ? '<div class="rlh-compare__top">&nbsp;</div>' : '';
      } else {
        var id = root.id + '-sel-' + i;
        top = '<div class="rlh-compare__top"><label class="rlh-compare__select-label" for="' + id + '">' + esc(labels.select) + '</label>' +
          '<div class="select rlh-compare__select-wrap"><select class="select__select rlh-compare__select" id="' + id + '" data-col="' + i + '">' +
          products.filter(function (o) { return o.handle !== current; }).map(function (o) {
            return '<option value="' + esc(o.handle) + '"' + (o.handle === p.handle ? ' selected' : '') + '>' + esc(o.name) + '</option>';
          }).join('') + '</select>' + CARET + '</div></div>';
      }
      var price = '';
      if (cfg.showPrice) {
        var from = p.varies ? '<small>' + esc(labels.from) + ' </small>' : '';
        if (p.compareAt) {
          // Dawn's own sale-price markup, so the theme's component-price.css styles it
          // (struck-through regular price, green sale price) exactly like the rest of the store.
          price = '<div class="rlh-compare__price price price--on-sale">' + from + '<div class="price__container"><div class="price__sale">' +
            '<span class="visually-hidden">' + esc(labels.regular) + ' </span><s class="price-item price-item--regular">' + esc(p.compareAt) + '</s> ' +
            '<span class="visually-hidden">' + esc(labels.sale) + ' </span><span class="price-item price-item--sale price-item--last">' + esc(p.price) + '</span>' +
            '</div></div></div>';
        } else {
          price = '<div class="rlh-compare__price">' + from + esc(p.price) + '</div>';
        }
      }
      var cta = isCurrent
        ? '<span class="button button--secondary rlh-compare__btn rlh-compare__btn--ghost">' + esc(labels.current) + '</span>'
        : '<a class="button' + (cfg.secondaryButtons ? ' button--secondary' : '') + ' rlh-compare__btn" href="' + esc(p.url) + '">' + esc(String(labels.view || '').replace('[name]', p.name)) + '</a>';
      var cls = 'rlh-compare__card' + (isCurrent ? ' is-current' : '') + (!table && i >= mobileCols ? ' is-m-hidden' : '');
      return '<div class="' + cls + '" style="--c:' + (i + 2) + '">' + top +
        '<div class="rlh-compare__media">' + (p.image ? '<img class="rlh-compare__img" src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="600" height="600">' : '') + '</div>' +
        '<h3 class="rlh-compare__title">' + (isCurrent ? esc(p.name) : '<a href="' + esc(p.url) + '">' + esc(p.name) + '</a>') + '</h3>' +
        (cfg.showTagline ? '<p class="rlh-compare__tagline">' + esc(p.tagline) + '</p>' : '') +
        price + stars(p) +
        (p.available ? '' : '<span class="rlh-compare__soldout">' + esc(labels.soldout) + '</span>') +
        cta + '</div>';
    }

    function render() {
      var active = document.activeElement;
      var focusedCol = active && root.contains(active) ? active.getAttribute('data-col') : null;

      var html = '';
      if (table) {
        grid.style.setProperty('--rlh-cols', Math.max(1, picks.length));
        if (!picks.length) {
          grid.innerHTML = '<p class="rlh-compare__empty">' + esc(labels.empty) + '</p>';
          return;
        }
        var count = String(labels.count || '').replace('[n]', picks.length).replace('[total]', products.length);
        html += '<div class="rlh-compare__corner" style="--c:1">' + esc(count) + '</div>';
      }
      html += picks.map(function (h, i) { return card(byHandle[h], i); }).join('');
      rows.forEach(function (row, r) {
        var values = picks.map(function (h) { return resolve(byHandle[h], row); });
        var plain = values.map(plainValue);
        var same = plain.every(function (x) { return x === plain[0]; }) ? ' is-same' : '';
        var editor = row.id ? ' data-shopify-editor-block="' + esc(JSON.stringify({ id: row.id, type: 'row' })) + '"' : '';
        html += '<div class="rlh-compare__label' + same + '" style="--r:' + (r + 2) + '"' + editor + '>' + esc(row.label) + '</div>';
        picks.forEach(function (h, i) {
          var p = byHandle[h];
          var v = values[i];
          var isCurrent = h === current && (table || i === 0);
          var cls = 'rlh-compare__cell' + (isCurrent ? ' is-current' : '') + (v ? '' : ' is-empty') + (!table && i >= mobileCols ? ' is-m-hidden' : '') + same;
          html += '<div class="' + cls + '" style="--c:' + (i + 2) + ';--r:' + (r + 2) + '">' +
            '<span class="visually-hidden">' + esc(p.name) + ': </span>' + (v ? renderValue(v) : '–') + '</div>';
        });
      });
      grid.innerHTML = html;

      if (focusedCol !== null) {
        var sel = grid.querySelector('select[data-col="' + focusedCol + '"]');
        if (sel) sel.focus();
      }
    }

    grid.addEventListener('change', function (e) {
      var sel = e.target.closest('select[data-col]');
      if (!sel) return;
      var col = +sel.getAttribute('data-col');
      var val = sel.value;
      var other = picks.indexOf(val);
      if (other !== -1 && other !== col) picks[other] = picks[col];
      picks[col] = val;
      render();
    });

    renderPicker();
    render();
  }

  function boot() { document.querySelectorAll('[data-rlh-compare]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
